import { createRouter, createRootRoute, createRoute, Outlet } from "@tanstack/react-router";
import { isMultiLocale } from "../translations/config";

// Import components
import { RootLayout } from "./routes/__root";
import { LocaleLayout } from "./routes/LocaleLayout";
import { LandingPage } from "./pages/landing/LandingPage";
import { LoginPage } from "./pages/auth/login/LoginPage";
import { SignupPage } from "./pages/auth/signup/SignupPage";
import { ForgotPasswordPage } from "./pages/auth/forgot-password/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/reset-password/ResetPasswordPage";
import { ProfilePage } from "./pages/profile/ProfilePage";
import { ProtectedRoute } from "./components/auth";
import { DashboardLayout } from "./components/dashboard";
import { NotFoundPage, ServerErrorPage } from "./pages/error";
import { DashboardContent } from "./pages/dashboard/components/DashboardContent";
import { AnalyticsPage } from "./pages/dashboard/AnalyticsPage";
import { ProjectsPage } from "./pages/dashboard/ProjectsPage";
import { TeamPage } from "./pages/dashboard/TeamPage";
import { SettingsPage } from "./pages/dashboard/SettingsPage";
import { BillingPage } from "./pages/dashboard/BillingPage";
import { RoadmapPage } from "./pages/dashboard/RoadmapPage";
import { CalendarPage } from "./pages/dashboard/CalendarPage";

/**
 * Dashboard Layout Wrapper
 *
 * Wraps dashboard routes with ProtectedRoute and DashboardLayout.
 */
function DashboardLayoutWrapper() {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  );
}

/**
 * Route Tree
 *
 * Define the application's route hierarchy using TanStack Router.
 * When isMultiLocale is true, public pages are also accessible under /$locale/.
 */

// Root route with layout and error handlers
const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ServerErrorPage,
});

// Index/Landing page
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

// Auth routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

// Protected routes
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

// Dashboard layout route (parent for all dashboard pages)
const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardLayoutWrapper,
});

// Dashboard index route (main dashboard page)
const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/",
  component: DashboardContent,
});

// Dashboard sub-routes
const analyticsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/projects",
  component: ProjectsPage,
});

const teamRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/team",
  component: TeamPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/settings",
  component: SettingsPage,
});

const billingRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/billing",
  component: BillingPage,
});

const calendarRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/calendar",
  component: CalendarPage,
});

const roadmapRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/roadmap",
  component: RoadmapPage,
});

// Public page routes (non-dashboard)
const publicRoutes = [
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  profileRoute,
];

// Dashboard route tree
const dashboardRouteTree = dashboardLayoutRoute.addChildren([
  dashboardIndexRoute,
  analyticsRoute,
  projectsRoute,
  teamRoute,
  settingsRoute,
  billingRoute,
  calendarRoute,
  roadmapRoute,
]);

/**
 * Build the route tree.
 * When multi-locale is enabled, add a /$locale layout route wrapping
 * duplicate public page routes so e.g. /nl/ and /nl/login work.
 */
function buildRouteTree() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const children: any[] = [...publicRoutes, dashboardRouteTree];

  if (isMultiLocale) {
    // Create locale layout route at /$locale
    const localeLayoutRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/$locale",
      component: LocaleLayout,
    });

    // Create locale-prefixed copies of public routes
    const localeIndex = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/",
      component: LandingPage,
    });

    const localeLogin = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/login",
      component: LoginPage,
    });

    const localeSignup = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/signup",
      component: SignupPage,
    });

    const localeForgotPassword = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/forgot-password",
      component: ForgotPasswordPage,
    });

    const localeResetPassword = createRoute({
      getParentRoute: () => localeLayoutRoute,
      path: "/reset-password",
      component: ResetPasswordPage,
    });

    children.push(
      localeLayoutRoute.addChildren([
        localeIndex,
        localeLogin,
        localeSignup,
        localeForgotPassword,
        localeResetPassword,
      ]),
    );
  }

  return rootRoute.addChildren(children);
}

const routeTree = buildRouteTree();

/**
 * Router Instance
 *
 * The main router for the application.
 * Export this to use in App.tsx with RouterProvider.
 */
export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ServerErrorPage,
});

// Type declaration for TypeScript
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export default router;
